import type { PhoneLink } from '@trips/shared/flight-estimate';
import { isFrozen, now } from './clock';

/**
 * Whether the phone is online, and the last moment it was. The flight estimate uses that to
 * start the plane moving: phones go into airplane mode around pushback, so a late flight isn't
 * shown taking off on schedule while it's still at the gate. Kept across restarts, so opening
 * the app mid-flight doesn't count the flight from then.
 */
const KEY = 'trips.lastOnlineAt';

let lastOnline: number | null = read();

function read(): number | null {
  try {
    const v = Number(localStorage.getItem(KEY));
    return Number.isFinite(v) && v > 0 ? v : null;
  } catch {
    return null;
  }
}

function online(): boolean {
  return isFrozen() || typeof navigator === 'undefined' || navigator.onLine;
}

function mark() {
  if (!online()) return;
  lastOnline = now();
  try {
    localStorage.setItem(KEY, String(lastOnline));
  } catch {
    // Storage off: this session still knows.
  }
}

if (typeof window !== 'undefined') {
  mark();
  // Going offline with the app open: it was online until just now.
  window.addEventListener('offline', () => {
    lastOnline = now();
    try {
      localStorage.setItem(KEY, String(lastOnline));
    } catch {
      // Storage off: this session still knows.
    }
  });
  window.addEventListener('online', mark);
  setInterval(mark, 30_000);
}

export function phoneLink(): PhoneLink {
  if (online()) {
    mark();
    return { online: true, offlineSince: null };
  }
  return { online: false, offlineSince: lastOnline };
}
